/**
 * `prop-types` isn't a project dependency (see the same note in
 * `AppShell.jsx` / `RoutePlaceholder.jsx`), so `react/prop-types` is
 * disabled for this whole file rather than pulling in a new dependency —
 * `DiscountCouponDetailsModal`'s `coupon` prop is accessed via many nested
 * property reads (`coupon.category`, `coupon.emails`, etc.) that the rule
 * flags individually, which a single `eslint-disable-next-line` on the
 * function signature does not cover.
 */
/* eslint-disable react/prop-types */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import LinearProgress from '@mui/material/LinearProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Grid from '@mui/material/Grid'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { PLAN_LABELS } from '../../../shared/constants/planLabels'
import {
  useDiscountCouponSearchMutation,
  useExpireCouponMutation,
  normalizeDiscountCoupons,
} from '../hooks/useDiscountCouponQuery'

const COUPON_CLASS_LABELS = {
  CL_REGULAR_TEMPORARY: 'Temporário',
  CL_REGULAR_PERMANENT: 'Vitalício',
  CL_COMBO: 'Combo',
  CL_INVITE: 'Recomendação',
}

const VALID_FOR_LABELS = {
  ONE_MONTH: '1 mês',
  TWO_MONTHS: '2 meses',
  THREE_MONTHS: '3 meses',
  SIX_MONTHS: '6 meses',
  YEAR: '1 ano',
  LIFETIME: 'Vitalício',
}

const DEPARTMENT_LABELS = {
  DEP_COMERCIAL: 'Comercial',
  DEP_COMERCIAL_UPSELL: 'Comercial-Upsell',
  DEP_MARKETING: 'Marketing',
  DEP_CUSTOMER_EXPERIENCE: 'Customer experience',
  DEP_ADMINISTRATION: 'Administração',
  DEP_TECHNOLOGY: 'Tecnologia',
}

const CUSTOMER_EXPERIENCE_REASON_LABELS = {
  PLAN_DEAL: 'Negociação de plano',
  EXPANSION: 'Expansão',
  IGPM: 'IGP-M',
}

function formatType(category, couponClass) {
  if (category === 'BY_SERVICE') {
    return 'Serviços adicionais'
  }
  return COUPON_CLASS_LABELS[couponClass]
}

function formatValidFor(validForConstant) {
  return VALID_FOR_LABELS[validForConstant]
}

function formatPlans(plansArray) {
  return plansArray.map((planConstant) => PLAN_LABELS[planConstant]).join(', ')
}

function formatDepartment(departmentConstant, reason) {
  if (departmentConstant === 'DEP_CUSTOMER_EXPERIENCE' && reason) {
    const translatedReason = CUSTOMER_EXPERIENCE_REASON_LABELS[reason]
    return `Customer experience - ${translatedReason}`
  }
  return DEPARTMENT_LABELS[departmentConstant]
}

function formatDiscountValue(discountValue) {
  return `R$ ${discountValue}`
}

function getQuantityLimitText(useQuantityLimit) {
  return useQuantityLimit ? ` - até ${useQuantityLimit} aplicações` : ''
}

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`
}

function joinRawArray(value) {
  return Array.isArray(value) ? value.join(',') : value
}

function isCouponExpired(coupon) {
  if (!coupon) return false
  if (coupon.expired) return true
  if (!coupon.dueDate) return false
  return new Date(coupon.dueDate) <= new Date()
}

function DiscountCouponDetailsModal({ coupon, open, onClose, onExpireNow }) {
  const [showMoreEmails, setShowMoreEmails] = useState(false)

  if (!coupon) return null

  const expired = isCouponExpired(coupon)
  const isByService = coupon.category === 'BY_SERVICE'
  const emails = coupon.emails || []

  function handleClose() {
    setShowMoreEmails(false)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Detalhes</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid size={6}>
            <Typography variant="caption" component="p">
              Data de início
            </Typography>
            <Typography variant="body2">{formatDate(coupon.fromDate)}</Typography>
          </Grid>
          <Grid size={expired ? 6 : 3}>
            <Typography variant="caption" component="p">
              Aplicação expira em
            </Typography>
            <Typography variant="body2">{formatDate(coupon.dueDate)}</Typography>
          </Grid>
          {!expired && (
            <Grid size={3} sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button size="small" variant="outlined" color="warning" onClick={onExpireNow}>
                Expirar agora
              </Button>
            </Grid>
          )}

          <Grid size={6}>
            <Typography variant="caption" component="p">
              Tipo
            </Typography>
            <Typography variant="body2">{formatType(coupon.category, coupon.couponClass) || '-'}</Typography>
          </Grid>
          {!isByService && (
            <Grid size={6}>
              <Typography variant="caption" component="p">
                Válido por
              </Typography>
              <Typography variant="body2">
                {(coupon.validFor && formatValidFor(coupon.validFor)) || '-'}
              </Typography>
            </Grid>
          )}
          {isByService && (
            <Grid size={6}>
              <Typography variant="caption" component="p">
                Código
              </Typography>
              <Typography variant="body2">{coupon.code || '-'}</Typography>
            </Grid>
          )}

          {!isByService && (
            <>
              <Grid size={6}>
                <Typography variant="caption" component="p">
                  Válido nos planos
                </Typography>
                <Typography variant="body2">
                  {(coupon.plans && coupon.plans.length && formatPlans(coupon.plans)) || '-'}
                </Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" component="p">
                  Válido na Periodicidade
                </Typography>
                <Typography variant="body2">{coupon.periodicity || '-'}</Typography>
              </Grid>
            </>
          )}

          <Grid size={6}>
            <Typography variant="caption" component="p">
              Válido para
            </Typography>
            {coupon.type !== 'ALL_TENANTS' && emails.length <= 5 && (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {emails.map((email) => (
                  <li key={email}>{email};</li>
                ))}
              </Box>
            )}
            {coupon.type !== 'ALL_TENANTS' && emails.length > 5 && (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {emails.slice(0, 5).map((email) => (
                  <li key={email}>{email};</li>
                ))}
                {!showMoreEmails && (
                  <Button
                    size="small"
                    endIcon={<ExpandMoreIcon />}
                    onClick={() => setShowMoreEmails(true)}
                  >
                    Ver todos
                  </Button>
                )}
                {showMoreEmails &&
                  emails.slice(5).map((email) => <li key={email}>{email};</li>)}
                {showMoreEmails && (
                  <Button
                    size="small"
                    endIcon={<ExpandLessIcon />}
                    onClick={() => setShowMoreEmails(false)}
                  >
                    Ver menos
                  </Button>
                )}
              </Box>
            )}
            {coupon.type === 'ALL_TENANTS' && (
              <Typography variant="body2">
                {`Todos os clientes${getQuantityLimitText(coupon.useQuantityLimit)}`}
              </Typography>
            )}
          </Grid>
          {!isByService && (
            <Grid size={6}>
              <Typography variant="caption" component="p">
                Código
              </Typography>
              <Typography variant="body2">{coupon.code || '-'}</Typography>
            </Grid>
          )}
          {isByService && (
            <Grid size={6}>
              <Typography variant="caption" component="p">
                Time
              </Typography>
              <Typography variant="body2">
                {(coupon.departament && formatDepartment(coupon.departament, coupon.reason)) || '-'}
              </Typography>
            </Grid>
          )}

          <Grid size={6}>
            <Typography variant="caption" component="p">
              Desconto
            </Typography>
            <Typography variant="body2">{formatDiscountValue(coupon.discountValue)}</Typography>
          </Grid>
          {!isByService && (
            <Grid size={6}>
              <Typography variant="caption" component="p">
                Time
              </Typography>
              <Typography variant="body2">
                {(coupon.departament && formatDepartment(coupon.departament, coupon.reason)) || '-'}
              </Typography>
            </Grid>
          )}
          {isByService && (
            <Grid size={6}>
              <Typography variant="caption" component="p">
                Vezes utilizado
              </Typography>
              <Typography variant="body2">{coupon.useQuantity || 'Nenhuma'}</Typography>
            </Grid>
          )}

          <Grid size={6}>
            <Typography variant="caption" component="p">
              Quem criou
            </Typography>
            <Typography variant="body2">{coupon.vendor || '-'}</Typography>
          </Grid>
          {!isByService && (
            <Grid size={6}>
              <Typography variant="caption" component="p">
                Vezes utilizado
              </Typography>
              <Typography variant="body2">{coupon.useQuantity || 'Nenhuma'}</Typography>
            </Grid>
          )}

          <Grid size={12}>
            <Typography variant="caption" component="p">
              Observações
            </Typography>
            <Typography variant="body2">{coupon.observation || '-'}</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}

function IndividualCouponsTab() {
  const navigate = useNavigate()
  const searchMutation = useDiscountCouponSearchMutation()
  const expireMutation = useExpireCouponMutation()

  const [tenantId, setTenantId] = useState('')
  const [code, setCode] = useState('')
  const [discountCoupons, setDiscountCoupons] = useState([])
  const [selectedDiscountCoupon, setSelectedDiscountCoupon] = useState(null)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  function notify(message) {
    setSnackbar({ open: true, message })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  function goTo(path) {
    navigate(path)
  }

  function handleSearch() {
    searchMutation.mutate(
      { tenantId: tenantId.trim(), code: code.trim() },
      {
        onSuccess: (data) => {
          setDiscountCoupons(normalizeDiscountCoupons(data))
        },
        onError: (error) => notify(`Ocorreu um erro: ${error?.message || 'Erro desconhecido'}`),
      },
    )
  }

  function handleOpenDetails(coupon) {
    setSelectedDiscountCoupon(coupon)
    setDetailsModalOpen(true)
  }

  function handleCloseDetails() {
    setDetailsModalOpen(false)
    setSelectedDiscountCoupon(null)
  }

  function handleExpireNow() {
    const couponCode = selectedDiscountCoupon?.code

    expireMutation.mutate(couponCode, {
      onSuccess: () => {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        setSelectedDiscountCoupon((current) => (current ? { ...current, expired: true, dueDate: yesterday } : current))
        notify('Cupom expirado com sucesso')
      },
      onError: (error) => notify(`Ocorreu um erro: ${error?.message || 'Erro desconhecido'}`),
    })
  }

  const isLoading = searchMutation.isPending

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Stack spacing={2}>
          <TextField
            id="tenantId"
            label="Digite um tenantId"
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            fullWidth
          />
          <TextField
            id="code"
            label="Digite um código de cupom de desconto"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={handleSearch} disabled={isLoading}>
              Buscar
            </Button>
            <Button variant="outlined" onClick={() => goTo('/workspace/discount-coupons/add')}>
              Adicionar
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {discountCoupons.length > 0 && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Mostrando: {discountCoupons.length}
        </Typography>
      )}

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tipo Cupom</TableCell>
              <TableCell>Tipo Desc</TableCell>
              <TableCell>TenantId</TableCell>
              <TableCell>Criação</TableCell>
              <TableCell>Início</TableCell>
              <TableCell>Validade</TableCell>
              <TableCell>Código</TableCell>
              <TableCell>Plano</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Valor de desconto</TableCell>
              <TableCell>Periodicidade</TableCell>
              <TableCell>Vendedor</TableCell>
              <TableCell>Vezes utilizados</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {discountCoupons.map((discountCoupon, index) => (
              <TableRow key={`${discountCoupon.code}-${index}`}>
                <TableCell>{discountCoupon.type}</TableCell>
                <TableCell>{discountCoupon.discountType}</TableCell>
                <TableCell>{joinRawArray(discountCoupon.tenantIds)}</TableCell>
                <TableCell>{formatDate(discountCoupon.createdAt)}</TableCell>
                <TableCell>{formatDate(discountCoupon.fromDate)}</TableCell>
                <TableCell>{formatDate(discountCoupon.dueDate)}</TableCell>
                <TableCell>{discountCoupon.code}</TableCell>
                <TableCell>{joinRawArray(discountCoupon.plans)}</TableCell>
                <TableCell>{discountCoupon.category}</TableCell>
                <TableCell>{discountCoupon.discountValue}</TableCell>
                <TableCell>{discountCoupon.periodicity}</TableCell>
                <TableCell>{discountCoupon.vendor}</TableCell>
                <TableCell>{discountCoupon.useQuantity}</TableCell>
                <TableCell>
                  <IconButton
                    aria-label="Ver detalhes"
                    onClick={() => handleOpenDetails(discountCoupon)}
                  >
                    <SearchIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DiscountCouponDetailsModal
        coupon={selectedDiscountCoupon}
        open={detailsModalOpen}
        onClose={handleCloseDetails}
        onExpireNow={handleExpireNow}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="info" variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default IndividualCouponsTab
